import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CalendarIcon, CheckCircleIcon, PlusIcon, TrashIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { doc, getDoc, collection, query, where, getDocs, addDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { format } from 'date-fns';
import { zhCN, enUS } from 'date-fns/locale';
import { useHeader } from '../../contexts/HeaderContext';
import { Dialog } from '@headlessui/react';
import TeacherAttendanceModal from './TeacherAttendanceModal';
import { dateToDatetimeLocal, datetimeLocalToDate } from '../../lib/utils';

interface Teacher {
    id: string;
    name: string;
    nameEn: string;
    photo: string;
    title: string;
    titleEn: string;
}

interface Program {
    id: string;
    title: string;
    subtitle: string;
    status: string;
    teacher: any[]; // 教师引用
}

interface AttendanceRecord {
    id: string;
    date: Date;
}

const TeacherAttendanceDetail: React.FC = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const programId = queryParams.get('programId');
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [program, setProgram] = useState<Program | null>(null);
    const [teachers, setTeachers] = useState<Teacher[]>([]);
    const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { setHeaderTheme } = useHeader();
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const [isCreatingAttendance, setIsCreatingAttendance] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [selectedAttendance, setSelectedAttendance] = useState<AttendanceRecord | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    
    // 新增状态管理
    const [isAttendanceModalOpen, setIsAttendanceModalOpen] = useState(false);
    const [selectedAttendanceId, setSelectedAttendanceId] = useState<string>('');
    const [selectedAttendanceDate, setSelectedAttendanceDate] = useState<Date | null>(null);
    const [teacherAttendanceStatuses, setTeacherAttendanceStatuses] = useState<any[]>([]);
    const [isExporting, setIsExporting] = useState(false);
    const [isRangeExportDialogOpen, setIsRangeExportDialogOpen] = useState(false);
    const [exportStartDate, setExportStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [exportEndDate, setExportEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [isRangeExporting, setIsRangeExporting] = useState(false);

    useEffect(() => {
        // 设置透明背景和白色文字
        setHeaderTheme({
            bgColor: 'bg-transparent',
            textColor: 'text-white'
        });

        return () => {
            // 恢复默认样式
            setHeaderTheme({
                bgColor: 'bg-white',
                textColor: 'text-[#142b89]'
            });
        };
    }, [setHeaderTheme]);

    useEffect(() => {
        const fetchProgramAndAttendance = async () => {
            if (!programId) {
                setError(t('program.idMissing'));
                setLoading(false);
                return;
            }

            try {
                // 获取项目信息
                const programDoc = await getDoc(doc(db, 'programs', programId));
                if (!programDoc.exists()) {
                    setError(t('program.notFound'));
                    setLoading(false);
                    return;
                }

                const programData = programDoc.data() as Program;
                programData.id = programDoc.id;
                setProgram(programData);
                
                // 获取与项目相关的教师信息
                const teacherRefs = programData.teacher || [];
                if (teacherRefs.length > 0) {
                    // 使用Promise.all并行获取所有教师信息
                    const teacherPromises = teacherRefs.map(teacherRef => getDoc(teacherRef));
                    const teacherDocs = await Promise.all(teacherPromises);
                    
                    const teachersData: Teacher[] = teacherDocs
                        .filter(doc => doc.exists())
                        .map(doc => {
                            const teacher = doc.data() as Teacher;
                            teacher.id = doc.id;
                            return teacher;
                        });
                        
                    setTeachers(teachersData);
                }
                
                // 获取教师考勤记录
                const attendanceQuery = query(
                    collection(db, 'teacher_attendance'),
                    where('program', '==', doc(db, 'programs', programId))
                );

                const attendanceSnapshot = await getDocs(attendanceQuery);
                const attendanceData = attendanceSnapshot.docs.map(doc => {
                    const data = doc.data();
                    // 处理日期字段，确保它是一个有效的日期
                    let dateObj: Date;
                    if (data.date && typeof data.date.toDate === 'function') {
                        // Firestore Timestamp类型
                        dateObj = data.date.toDate();
                    } else {
                        // 如果没有合适的日期字段或无法转换，使用当前时间
                        console.warn('考勤记录缺少有效的日期字段:', doc.id);
                        dateObj = new Date();
                    }
                    
                    return {
                        id: doc.id,
                        date: dateObj
                    } as AttendanceRecord;
                });

                // 按日期排序（从新到旧）
                attendanceData.sort((a, b) => b.date.getTime() - a.date.getTime());
                setAttendanceRecords(attendanceData);
            } catch (err) {
                console.error('获取项目和考勤信息失败:', err);
                setError(t('program.fetchError'));
            } finally {
                setLoading(false);
            }
        };

        fetchProgramAndAttendance();
    }, [programId, t]);

    // 创建考勤记录，默认所有教师都是出席状态
    const handleCreateAttendance = async () => {
        if (!programId) {
            console.error('没有项目ID');
            return;
        }
        
        try {
            setIsCreatingAttendance(true);
            
            // 为所有教师创建默认出席记录
            const teacherRecords = teachers.map(teacher => ({
                teacher: doc(db, 'teachers', teacher.id),
                status: 'present', // 默认出席状态
                notes: ''
            }));
            
            // 创建考勤记录，包含所有教师的默认出席状态
            const attendanceData = {
                program: doc(db, 'programs', programId),
                date: Timestamp.fromDate(selectedDate),
                records: teacherRecords
            };
            
            // 添加到 teacher_attendance 集合
            const docRef = await addDoc(collection(db, 'teacher_attendance'), attendanceData);
            
            // 添加成功后，添加到记录列表
            const newRecord: AttendanceRecord = {
                id: docRef.id,
                date: selectedDate
            };
            
            setAttendanceRecords(prev => [newRecord, ...prev]);
            setIsCreateDialogOpen(false);
            
        } catch (error) {
            console.error('创建考勤记录失败:', error);
            alert(t('attendance.createError'));
        } finally {
            setIsCreatingAttendance(false);
        }
    };

    // 处理考勤记录点击，改为打开Modal弹窗
    const handleAttendanceClick = async (attendanceId: string) => {
        try {
            // 获取考勤记录详情
            const attendanceDoc = await getDoc(doc(db, 'teacher_attendance', attendanceId));
            if (!attendanceDoc.exists()) {
                alert(t('attendance.notFound'));
                return;
            }

            const attendanceData = attendanceDoc.data();
            let attendanceDate = new Date();
            
            // 设置日期
            if (attendanceData.date) {
                attendanceDate = attendanceData.date.toDate();
            }
            
            // 获取教师状态
            if (attendanceData.records && Array.isArray(attendanceData.records)) {
                // 创建教师ID到教师信息的映射，避免重复请求
                const teacherMap = teachers.reduce((map, teacher) => {
                    map[teacher.id] = teacher;
                    return map;
                }, {} as Record<string, Teacher>);

                // 处理考勤记录
                const teacherStatuses = attendanceData.records
                    .filter(record => record.teacher)
                    .map(record => {
                        // 从参照中提取教师ID - 处理Firestore引用对象
                        let teacherId;
                        if (record.teacher.id) {
                            // 如果是已经解析过的对象
                            teacherId = record.teacher.id;
                        } else if (record.teacher.path) {
                            // 如果是Firestore引用对象
                            const pathParts = record.teacher.path.split('/');
                            teacherId = pathParts[pathParts.length - 1];
                        } else {
                            // 无法识别的格式
                            return null;
                        }
                        
                        // 从本地缓存的teachers数组中获取教师信息
                        const teacherData = teacherMap[teacherId];
                        
                        // 如果找到了教师数据，直接使用，不需要额外请求
                        if (teacherData) {
                            return {
                                teacherId: teacherId,
                                name: teacherData.name,
                                nameEn: teacherData.nameEn || teacherData.name,
                                photo: teacherData.photo,
                                status: record.status || 'absent',
                                notes: record.notes || ''
                            };
                        }
                        
                        // 如果在本地找不到（极少情况），返回null
                        return null;
                    })
                    .filter(item => item !== null);
                
                // 设置状态
                setSelectedAttendanceId(attendanceId);
                setSelectedAttendanceDate(attendanceDate);
                setTeacherAttendanceStatuses(teacherStatuses);
                setIsAttendanceModalOpen(true);
            } else {
                // 设置状态
                setSelectedAttendanceId(attendanceId);
                setSelectedAttendanceDate(attendanceDate);
                setTeacherAttendanceStatuses([]);
                setIsAttendanceModalOpen(true);
            }
            
        } catch (error) {
            console.error('获取考勤详情失败:', error);
            alert(t('attendance.fetchError'));
        }
    };

    // 处理考勤保存成功
    const handleAttendanceSaveSuccess = () => {
        // 可以选择刷新考勤记录列表，或者不做任何操作
        // 这里暂时不执行刷新操作
    };

    // 处理删除考勤记录
    const handleDeleteDialogOpen = (record: AttendanceRecord, e: React.MouseEvent) => {
        e.stopPropagation(); // 阻止事件冒泡，避免触发考勤详情跳转
        setSelectedAttendance(record);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteAttendance = async () => {
        if (!selectedAttendance) return;
        
        try {
            setIsDeleting(true);
            
            // 从 teacher_attendance 集合中删除记录
            await deleteDoc(doc(db, 'teacher_attendance', selectedAttendance.id));
            
            // 从列表中移除已删除的记录
            setAttendanceRecords(prev => prev.filter(record => record.id !== selectedAttendance.id));
            setIsDeleteDialogOpen(false);
            setSelectedAttendance(null);
        } catch (error) {
            console.error('删除考勤记录失败:', error);
            alert(t('attendance.deleteError'));
        } finally {
            setIsDeleting(false);
        }
    };

    const parseDateInput = (value: string, endOfDay = false) => {
        const [year, month, day] = value.split('-').map(Number);
        return endOfDay
            ? new Date(year, month - 1, day, 23, 59, 59, 999)
            : new Date(year, month - 1, day, 0, 0, 0, 0);
    };

    const getAttendanceDate = (attendanceData: any) => {
        if (attendanceData.date && typeof attendanceData.date.toDate === 'function') {
            return attendanceData.date.toDate();
        }

        return new Date();
    };

    const getStatusText = (status: string) => {
        switch(status) {
            case 'present': return t('attendance.present');
            case 'absent': return t('attendance.absent');
            case 'late': return t('attendance.late');
            default: return status;
        }
    };

    const resolveTeacherId = (teacherRef: any) => {
        if (teacherRef?.id) {
            return teacherRef.id;
        }

        if (teacherRef?.path) {
            const pathParts = teacherRef.path.split('/');
            return pathParts[pathParts.length - 1];
        }

        return null;
    };

    const buildCsvRows = (attendanceData: any, attendanceDate: Date) => {
        const formattedDate = format(attendanceDate, 'yyyy-MM-dd');
        const teacherMap = teachers.reduce((map, teacher) => {
            map[teacher.id] = teacher;
            return map;
        }, {} as Record<string, Teacher>);

        if (!attendanceData.records || !Array.isArray(attendanceData.records)) {
            return [];
        }

        return attendanceData.records
            .filter(record => record.teacher)
            .map(record => {
                const teacherId = resolveTeacherId(record.teacher);
                if (!teacherId) {
                    return null;
                }

                const teacherData = teacherMap[teacherId];
                if (!teacherData) {
                    return null;
                }

                return [
                    formattedDate,
                    teacherData.name,
                    teacherData.nameEn || teacherData.name,
                    i18n.language === 'zh' ? teacherData.title : teacherData.titleEn,
                    getStatusText(record.status || 'absent'),
                    record.notes || ''
                ];
            })
            .filter(item => item !== null);
    };

    const escapeCsvField = (field: string) => {
        if (field === null || field === undefined) return '';
        const str = String(field);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
    };

    const downloadCsv = (rows: any[][], fileName: string) => {
        const headerRow = ['date', 'name', 'nameEn', 'title', 'status', 'notes'];
        const csvContent = [
            headerRow.map(escapeCsvField).join(','),
            ...rows.map(row => row.map(escapeCsvField).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleOpenRangeExportDialog = () => {
        if (attendanceRecords.length > 0) {
            const timestamps = attendanceRecords.map(record => record.date.getTime());
            setExportStartDate(format(new Date(Math.min(...timestamps)), 'yyyy-MM-dd'));
            setExportEndDate(format(new Date(Math.max(...timestamps)), 'yyyy-MM-dd'));
        }

        setIsRangeExportDialogOpen(true);
    };

    // 导出考勤记录为CSV
    const handleExportCSV = async (attendanceId: string, e: React.MouseEvent) => {
        e.stopPropagation(); // 阻止事件冒泡，避免触发考勤详情
        
        try {
            setIsExporting(true);
            
            const attendanceDoc = await getDoc(doc(db, 'teacher_attendance', attendanceId));
            if (!attendanceDoc.exists()) {
                alert(t('attendance.notFound'));
                return;
            }

            const attendanceData = attendanceDoc.data();
            const attendanceDate = getAttendanceDate(attendanceData);
            const formattedDate = format(attendanceDate, 'yyyy-MM-dd');
            const rows = buildCsvRows(attendanceData, attendanceDate);

            if (rows.length === 0) {
                alert(t('attendance.noRecords'));
                return;
            }

            const programName = program?.title?.replace(/\s+/g, '_') || 'program';
            downloadCsv(rows, `${programName}_attendance_${formattedDate}.csv`);
        } catch (error) {
            console.error('导出考勤记录失败:', error);
            alert(t('common.error'));
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportRangeCSV = async () => {
        if (!exportStartDate || !exportEndDate) {
            alert(t('attendance.invalidDateRange'));
            return;
        }

        const startDate = parseDateInput(exportStartDate);
        const endDate = parseDateInput(exportEndDate, true);

        if (startDate.getTime() > endDate.getTime()) {
            alert(t('attendance.invalidDateRange'));
            return;
        }

        const recordsInRange = attendanceRecords.filter(record =>
            record.date.getTime() >= startDate.getTime() &&
            record.date.getTime() <= endDate.getTime()
        );

        if (recordsInRange.length === 0) {
            alert(t('attendance.noRecordsInRange'));
            return;
        }

        try {
            setIsRangeExporting(true);

            const snapshots = await Promise.all(
                recordsInRange.map(record => getDoc(doc(db, 'teacher_attendance', record.id)))
            );

            const rows = snapshots.flatMap(snapshot => {
                if (!snapshot.exists()) {
                    return [];
                }

                const attendanceData = snapshot.data();
                return buildCsvRows(attendanceData, getAttendanceDate(attendanceData));
            });

            if (rows.length === 0) {
                alert(t('attendance.noRecordsInRange'));
                return;
            }

            const programName = program?.title?.replace(/\s+/g, '_') || 'program';
            downloadCsv(rows, `${programName}_attendance_${exportStartDate}_to_${exportEndDate}.csv`);
            setIsRangeExportDialogOpen(false);
        } catch (error) {
            console.error('按时间段导出考勤记录失败:', error);
            alert(t('common.error'));
        } finally {
            setIsRangeExporting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-800 pt-20 px-4 pb-12 pt-48">
            <div className="max-w-4xl mx-auto">
                {/* 标题区域 */}
                <div className="flex items-center mb-8">
                    <button 
                        onClick={() => navigate(-1)}
                        className="text-white/80 hover:text-white mr-4"
                    >
                        <ArrowLeftIcon className="h-6 w-6" />
                    </button>
                    <h1 className="text-2xl font-bold text-white">
                        {i18n.language === 'zh' ? program?.title : program?.subtitle || program?.title}
                    </h1>
                    {program?.status !== 'active' && (
                        <span className="ml-3 px-2 py-1 text-xs bg-yellow-600 text-white rounded-full">
                            {program?.status === 'inactive' ? t('program.inactive') : program?.status}
                        </span>
                    )}
                </div>

                {/* 教师列表 */}
                {!loading && teachers.length > 0 && (
                    <div className="mb-8">
                        <h2 className="text-xl font-semibold text-white mb-4">{i18n.language === 'zh' ? '教师列表' : 'Teacher List'}</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                            {teachers.map(teacher => (
                                <div key={teacher.id} className="bg-white/10 rounded-lg p-4 flex items-center">
                                    {teacher.photo ? (
                                        <img 
                                            src={teacher.photo} 
                                            alt={i18n.language === 'zh' ? teacher.name : teacher.nameEn || teacher.name} 
                                            className="w-12 h-12 rounded-full object-cover mr-3"
                                        />
                                    ) : (
                                        <div className="w-12 h-12 rounded-full bg-blue-500/30 flex items-center justify-center mr-3">
                                            <span className="text-white text-lg">{teacher.name?.charAt(0)}</span>
                                        </div>
                                    )}
                                    <div>
                                        <div className="text-white font-medium">
                                            {i18n.language === 'zh' ? teacher.name : teacher.nameEn || teacher.name}
                                        </div>
                                        <div className="text-blue-200 text-sm">
                                            {i18n.language === 'zh' ? teacher.title : teacher.titleEn || teacher.title}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 创建考勤按钮 */}
                <div className="mb-6">
                    {program?.status === 'active' ? (
                        <button
                            onClick={() => setIsCreateDialogOpen(true)}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg p-4 flex items-center justify-center transition-colors"
                        >
                            <PlusIcon className="h-5 w-5 mr-2" />
                            <span>{t('attendance.createTeacherAttendance')}</span>
                        </button>
                    ) : (
                        <div className="w-full bg-gray-600 text-white rounded-lg p-4 flex items-center justify-center opacity-70">
                            <span>{t('attendance.cannotCreateInactive')}</span>
                        </div>
                    )}
                </div>

                {/* 考勤记录标题 */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                    <h2 className="text-xl font-semibold text-white">{t('attendance.teacherRecords')}</h2>
                    <button
                        onClick={handleOpenRangeExportDialog}
                        className="w-full sm:w-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg flex items-center justify-center transition-colors"
                        disabled={loading || attendanceRecords.length === 0 || isRangeExporting}
                    >
                        {isRangeExporting ? (
                            <span className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                        ) : (
                            <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                        )}
                        <span>{t('attendance.exportRangeCsv')}</span>
                    </button>
                </div>

                {/* 内容区域 */}
                {loading ? (
                    <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
                    </div>
                ) : error ? (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                        <p className="text-red-200 text-center">{error}</p>
                    </div>
                ) : attendanceRecords.length === 0 ? (
                    <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                        <p className="text-white/60 text-center">{t('attendance.noTeacherRecords')}</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {attendanceRecords.map((record, index) => (
                            <motion.div
                                key={record.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: index * 0.1 }}
                                className="bg-white/10 border border-white/10 rounded-lg p-4 flex items-center justify-between cursor-pointer hover:bg-white/20 transition-colors"
                                onClick={() => handleAttendanceClick(record.id)}
                            >
                                <div className="flex items-center">
                                    <CalendarIcon className="h-5 w-5 text-blue-300 mr-3" />
                                    <div>
                                        <div className="text-white">
                                            {format(record.date, 'PPP', {
                                                locale: i18n.language === 'zh' ? zhCN : enUS
                                            })}
                                        </div>
                                        <div className="text-white/60 text-sm">
                                            {format(record.date, 'HH:mm')}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* 操作按钮 */}
                                <div className="flex items-center space-x-2">
                                    {/* 导出CSV按钮 */}
                                    <button
                                        onClick={(e) => handleExportCSV(record.id, e)}
                                        className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center transition-colors"
                                        title={t('attendance.exportCsv')}
                                        disabled={isExporting}
                                    >
                                        {isExporting ? (
                                            <span className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                        ) : (
                                            <ArrowDownTrayIcon className="h-5 w-5" />
                                        )}
                                    </button>
                                    
                                    {/* 删除按钮 */}
                                    {program?.status === 'active' && (
                                        <button
                                            onClick={(e) => handleDeleteDialogOpen(record, e)}
                                            className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center transition-colors"
                                            title={t('attendance.delete')}
                                        >
                                            <TrashIcon className="h-5 w-5" />
                                        </button>
                                    )}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
            
            {/* 创建考勤对话框 */}
            <Dialog 
                open={isCreateDialogOpen} 
                onClose={() => setIsCreateDialogOpen(false)}
                className="relative z-50"
            >
                <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="bg-white rounded-lg p-6 w-full max-w-md">
                        <Dialog.Title className="text-lg font-medium text-gray-900 mb-4">
                            {t('attendance.createTeacherAttendance')}
                        </Dialog.Title>
                        
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('attendance.selectDate')}
                            </label>
                            <input 
                                type="datetime-local" 
                                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                value={dateToDatetimeLocal(selectedDate)}
                                onChange={(e) => setSelectedDate(datetimeLocalToDate(e.target.value))}
                            />
                        </div>
                        
                        <div className="flex justify-end space-x-2">
                            <button
                                type="button"
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                                onClick={() => setIsCreateDialogOpen(false)}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                type="button"
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400"
                                onClick={handleCreateAttendance}
                                disabled={isCreatingAttendance}
                            >
                                {isCreatingAttendance ? t('common.loading') : t('common.confirm')}
                            </button>
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>

            {/* 时间段导出对话框 */}
            <Dialog
                open={isRangeExportDialogOpen}
                onClose={() => setIsRangeExportDialogOpen(false)}
                className="relative z-50"
            >
                <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="bg-white rounded-lg p-6 w-full max-w-md">
                        <Dialog.Title className="text-lg font-medium text-gray-900 mb-4">
                            {t('attendance.exportDateRange')}
                        </Dialog.Title>

                        <div className="space-y-4 mb-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('attendance.startDate')}
                                </label>
                                <input
                                    type="date"
                                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    value={exportStartDate}
                                    onChange={(e) => setExportStartDate(e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('attendance.endDate')}
                                </label>
                                <input
                                    type="date"
                                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    value={exportEndDate}
                                    onChange={(e) => setExportEndDate(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="flex justify-end space-x-2">
                            <button
                                type="button"
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                                onClick={() => setIsRangeExportDialogOpen(false)}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                type="button"
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400"
                                onClick={handleExportRangeCSV}
                                disabled={isRangeExporting}
                            >
                                {isRangeExporting ? t('attendance.exporting') : t('common.confirm')}
                            </button>
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>
            
            {/* 删除考勤对话框 */}
            <Dialog 
                open={isDeleteDialogOpen} 
                onClose={() => setIsDeleteDialogOpen(false)}
                className="relative z-50"
            >
                <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="bg-white rounded-lg p-6 w-full max-w-md">
                        <Dialog.Title className="text-xl font-medium text-gray-900 mb-2">
                            {t('attendance.confirmDelete')}
                        </Dialog.Title>
                        
                        <p className="text-gray-700 mb-6">
                            {t('attendance.deleteWarning')}
                        </p>
                        
                        <div className="flex justify-end space-x-2">
                            <button
                                type="button"
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                                onClick={() => setIsDeleteDialogOpen(false)}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                type="button"
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-400"
                                onClick={handleDeleteAttendance}
                                disabled={isDeleting}
                            >
                                {isDeleting ? t('common.loading') : t('common.delete')}
                            </button>
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>
            
            {/* 考勤Modal弹窗 */}
            {isAttendanceModalOpen && selectedAttendanceId && selectedAttendanceDate && (
                <TeacherAttendanceModal
                    isOpen={isAttendanceModalOpen}
                    onClose={() => setIsAttendanceModalOpen(false)}
                    teachers={teachers}
                    attendanceId={selectedAttendanceId}
                    attendanceDate={selectedAttendanceDate}
                    existingRecords={teacherAttendanceStatuses}
                    onSaveSuccess={handleAttendanceSaveSuccess}
                />
            )}
        </div>
    );
};

export default TeacherAttendanceDetail; 
